/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/browser';
import { useStorageState } from 'react-storage-hooks';
import { Box } from 'grommet';
import styled from 'styled-components';
import { noop } from 'lodash';

import { useThemeColor as c } from '@blend/alchemy';
import {
  defaultPageSize,
  FiltersMenu,
  PipelineTable,
  useColumns,
  useNoDataComponentType,
  usePipelineReducer,
} from '@blend/enterprise-components';

import * as RonApi from 'blb-api/ron';
import { BlendBridge } from 'blb-types/blendBridge';
import { getColumnConfigurations, getColumnSchemas } from 'blb-components/AuthenticatedApp/RON/NotaryPipeline/configs';
import { PipelineColumnHeader } from 'blb-components/AuthenticatedApp/PipelinePage/NewPipeline/PipelineColumnHeader';
import { PipelineSearchResult } from 'blb-components/AuthenticatedApp/RON/NotaryPipeline/PipelineSearchResult';
import { TypeaheadSearch } from 'blb-components/AuthenticatedApp/TypeaheadSearch';
import { searchWidth } from 'blb-styles/constants';
import { DEFAULT_PIPELINE_STATE, getSortKey, PipelineState } from 'blb-utils/pipeline';

import { PipelineStateContext } from '../../PipelinePage/NewPipeline/PipelineStateContext';
import { NotariesPageHeader } from './NotariesPageHeader';
import * as mappers from './mappers';

const NOTARY_DEFAULT_SORT_FIELD = 'Notary.name';
const NOTARY_PIPELINE_STATE_KEY = 'pipelineState.notary';

const FilterMenuWrapper = styled(Box)`
  background-color: ${c('cloud10')};
  border-bottom: 1px ${c('cloud50')};
  padding: 0 40px;
`;

const PageLayout = styled.div`
  height: 100%;
  flex-direction: column;
  display: flex;
`;

const PipelineWrapper = styled(Box)`
  flex: 1 1;
`;
export const NotaryPipelinePage: React.FC = ({}) => {
  const [pipelineState, setPipelineState] = useStorageState<PipelineState>(
    sessionStorage,
    NOTARY_PIPELINE_STATE_KEY,
    DEFAULT_PIPELINE_STATE,
  );

  const [pipelineTabs, setPipelineTabs] = useState<{
    activeTabId: string;
    tabs: BlendBridge.PipelineTab[];
  } | null>(null);

  const columnConfigs = getColumnConfigurations();
  const columnSchemas = getColumnSchemas();

  const pipelineReducer = usePipelineReducer(NOTARY_DEFAULT_SORT_FIELD, { columnSchema: columnSchemas });
  const { state, getState, updateOffset, fetchOnStart, fetchOnSuccess, fetchOnError } = pipelineReducer;

  const getUpdatedSortAndOffset = React.useCallback(() => {
    const { sortObj, offset } = getState();
    const sortKey = getSortKey(columnSchemas, sortObj.sortField);
    setPipelineState({
      ...pipelineState,
      offset,
      sortDir: sortObj.sortDir,
      sortField: sortKey,
    });
  }, [getState]);

  const columns = useColumns(columnConfigs, columnSchemas, true, () => {}, noop, PipelineColumnHeader);

  const isRequestLockedRef: React.MutableRefObject<boolean> = React.useRef<boolean>(false);

  // required to ensure rows get sorted properly
  const getRowKey = (_state: void, rowData: { original: { _id: string } }) => ({ key: rowData.original._id });

  useEffect(() => {
    // Fetch pipelineTabs which in return renders <FiltersMenu /> if tabs exist.
    // Otherwise default to original pipeline view
    const fetchPipelineTabs = async () => {
      try {
        const tabs = mappers.mapNotaryCountsByStatusResponseToPipelineTabs(await RonApi.getNotaryCountsByStatus());
        setPipelineTabs({
          activeTabId: 'all',
          tabs,
        });
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchPipelineTabs();
  }, []);

  const fetchPipelineData = React.useCallback(async () => {
    if (isRequestLockedRef.current || !pipelineTabs) {
      return;
    }

    isRequestLockedRef.current = true;
    fetchOnStart();

    try {
      const response = await RonApi.fetchNotaryList({
        limit: defaultPageSize,
        offset: pipelineState.offset,
        status: mappers.mapTabIdToUserStatus(pipelineTabs.activeTabId),
      });
      fetchOnSuccess(mappers.mapGetUserListResponseToPipelineTopic(response));
    } catch (error) {
      Sentry.captureException(error);
      fetchOnError();
    } finally {
      isRequestLockedRef.current = false;
    }
  }, [fetchOnStart, fetchOnSuccess, fetchOnError, pipelineState, pipelineTabs]);

  useEffect(() => {
    if (pipelineTabs != null) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetchPipelineData();
    }
  }, [pipelineTabs, pipelineState]);

  const noDataType = useNoDataComponentType(state.isFetching);

  const getEmptyTypeMap = (subjectsNoun = 'notaries') => {
    return {
      empty: {
        header: `Empty list`,
      },
      filter: {
        header: 'Try different filters to expand your search',
        subtext: `We didn't find any ${subjectsNoun} that match those filters.`,
      },
      loading: {
        header: `Loading ${subjectsNoun}...`,
      },
    };
  };

  const setToLoadingState = () => fetchOnSuccess({ topics: [], totalCount: 0 });

  const getFiltersMenuComponent = () =>
    pipelineTabs && (
      <FilterMenuWrapper>
        <FiltersMenu
          filterList={pipelineTabs.tabs.map(tab => {
            return {
              detail: tab.subjectCount?.toString() || '',
              id: tab.id,
              label: tab.name,
            };
          })}
          onSelect={tab => {
            // reset offset to 0 upon tab change
            // this update control the pipeline data fetch
            setPipelineState({
              ...pipelineState,
              offset: 0,
            });
            // the following line updates the footer indicator
            updateOffset(0);
            setPipelineTabs({
              ...pipelineTabs,
              activeTabId: tab.id,
            });
            setToLoadingState();
          }}
          selectedFilterId={pipelineTabs.activeTabId}
        />
      </FilterMenuWrapper>
    );

  const search = async (query: string) => {
    const result = await RonApi.fetchNotaryList({
      limit: 10,
      offset: 0,
      search: query,
    });
    return {
      data: result.users,
    };
  };
  const getSearchComponent = () => {
    return (
      <Box align="end" justify="end" margin={{ right: 'xlarge', top: 'small' }}>
        <Box width={`${searchWidth}px`}>
          <TypeaheadSearch
            ResultComponent={PipelineSearchResult}
            id="pipeline-search"
            name="Pipeline"
            onResultClick={noop}
            placeholder="Search for notaries"
            searchFunction={search}
          />
        </Box>
      </Box>
    );
  };

  const contextValue = React.useMemo(() => pipelineReducer, [pipelineReducer]);
  return (
    <PipelineStateContext.Provider value={contextValue}>
      <PageLayout>
        <NotariesPageHeader />
        {getFiltersMenuComponent()}
        {getSearchComponent()}
        <PipelineWrapper>
          <PipelineTable
            canSelectRows={false}
            columns={columns}
            emptyType={noDataType}
            emptyTypeMap={getEmptyTypeMap()}
            errorFetchingData={state.errorLoadingLoans}
            fetchPipelineTopics={getUpdatedSortAndOffset}
            getTrProps={getRowKey}
            hideTotalCount={true}
            offset={state.offset}
            selectionsMap={undefined}
            setSelectionState={undefined}
            tableData={state.rows}
            tableDataNoun="notary"
            tableName="NotaryPipeline"
            totalDataCount={state.totalCount}
            updatePipelineOffset={updateOffset}
          />
        </PipelineWrapper>
      </PageLayout>
    </PipelineStateContext.Provider>
  );
};
